import XLSX from 'xlsx-js-style'
import moment from 'moment'

// ===== Palette (อิงสีระบบ web-doctor) =====
const COLORS = {
  primary: '1B4B63',     // primaryText
  accent: '45C1B8',      // primaryButton
  zebra: 'F5F8FA',
  total: 'E8F5F3',
  kpiBg: 'F0F7F6',
  border: 'D0D7DC',
  subText: '8C8C8C',
}

const border = (rgb = COLORS.border) => ({
  top: { style: 'thin', color: { rgb } },
  bottom: { style: 'thin', color: { rgb } },
  left: { style: 'thin', color: { rgb } },
  right: { style: 'thin', color: { rgb } },
})

const STYLE = {
  title: {
    font: { bold: true, sz: 18, color: { rgb: 'FFFFFF' } },
    fill: { patternType: 'solid', fgColor: { rgb: COLORS.primary } },
    alignment: { horizontal: 'center', vertical: 'center' },
  },
  subtitle: {
    font: { sz: 11, color: { rgb: COLORS.subText }, italic: true },
    alignment: { horizontal: 'center', vertical: 'center' },
  },
  section: {
    font: { bold: true, sz: 13, color: { rgb: 'FFFFFF' } },
    fill: { patternType: 'solid', fgColor: { rgb: COLORS.accent } },
    alignment: { horizontal: 'left', vertical: 'center', indent: 1 },
  },
  kpiLabel: {
    font: { sz: 10, color: { rgb: COLORS.subText } },
    fill: { patternType: 'solid', fgColor: { rgb: COLORS.kpiBg } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: border(),
  },
  kpiValue: {
    font: { bold: true, sz: 20, color: { rgb: COLORS.primary } },
    fill: { patternType: 'solid', fgColor: { rgb: COLORS.kpiBg } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: border(),
  },
  th: {
    font: { bold: true, color: { rgb: 'FFFFFF' } },
    fill: { patternType: 'solid', fgColor: { rgb: COLORS.primary } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: border(COLORS.primary),
  },
  td: (zebra) => ({
    fill: zebra ? { patternType: 'solid', fgColor: { rgb: COLORS.zebra } } : undefined,
    alignment: { vertical: 'center' },
    border: border(),
  }),
  tdNum: (zebra) => ({
    fill: zebra ? { patternType: 'solid', fgColor: { rgb: COLORS.zebra } } : undefined,
    alignment: { horizontal: 'right', vertical: 'center' },
    border: border(),
    numFmt: '0.0',
  }),
  tdInt: (zebra) => ({
    fill: zebra ? { patternType: 'solid', fgColor: { rgb: COLORS.zebra } } : undefined,
    alignment: { horizontal: 'right', vertical: 'center' },
    border: border(),
    numFmt: '0',
  }),
  total: {
    font: { bold: true, color: { rgb: COLORS.primary } },
    fill: { patternType: 'solid', fgColor: { rgb: COLORS.total } },
    alignment: { vertical: 'center' },
    border: border(),
  },
  totalNum: {
    font: { bold: true, color: { rgb: COLORS.primary } },
    fill: { patternType: 'solid', fgColor: { rgb: COLORS.total } },
    alignment: { horizontal: 'right', vertical: 'center' },
    border: border(),
    numFmt: '0.0',
  },
  totalInt: {
    font: { bold: true, color: { rgb: COLORS.primary } },
    fill: { patternType: 'solid', fgColor: { rgb: COLORS.total } },
    alignment: { horizontal: 'right', vertical: 'center' },
    border: border(),
    numFmt: '0',
  },
}

// ===== Cell helpers =====
const tCell = (v, s) => ({ v, t: 's', s })
const nCell = (v, s) => ({ v, t: 'n', s })
const blank = () => null

const buildSheet = (matrix, opts = {}) => {
  const ws = {}
  let maxR = -1
  let maxC = -1
  matrix.forEach((row, r) => {
    row.forEach((c, ci) => {
      if (c == null) return
      const addr = XLSX.utils.encode_cell({ r, c: ci })
      ws[addr] = c
      if (r > maxR) maxR = r
      if (ci > maxC) maxC = ci
    })
  })
  if (maxR >= 0) ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: maxR, c: maxC } })
  if (opts.cols) ws['!cols'] = opts.cols
  if (opts.merges) ws['!merges'] = opts.merges
  if (opts.rows) ws['!rows'] = opts.rows
  if (opts.autofilter) ws['!autofilter'] = opts.autofilter
  if (opts.freeze) ws['!views'] = [{ state: 'frozen', ySplit: opts.freeze.row, xSplit: opts.freeze.col || 0 }]
  return ws
}

// ===== Computation helpers =====
const minFmt = (sec) => Math.round((sec / 60) * 10) / 10
const median = (arr) => {
  if (!arr.length) return 0
  const s = [...arr].sort((a, b) => a - b)
  const m = Math.floor(s.length / 2)
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2
}
const DOW_TH = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส']
const DOW_TH_FULL = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์']
const periodOf = (h) => {
  if (h >= 6 && h < 12) return 'เช้า'
  if (h >= 12 && h < 17) return 'บ่าย'
  if (h >= 17 && h < 21) return 'เย็น'
  return 'ดึก'
}

const computeMonth = (calls, monthIdx, patientFirstMonth) => {
  const durSecs = calls.map((c) => c.durSec)
  const totalSec = durSecs.reduce((s, v) => s + v, 0)
  const patientIds = new Set(calls.map((c) => c.patientId).filter(Boolean))
  const doctorNames = new Set(calls.map((c) => c.doctorName).filter(Boolean))
  const days = new Set(calls.map((c) => moment(c.startMs).format('YYYY-MM-DD')))

  const patientCallCount = {}
  calls.forEach((c) => {
    patientCallCount[c.patientId] = (patientCallCount[c.patientId] || 0) + 1
  })
  const newPatients = [...patientIds].filter((pid) => patientFirstMonth[pid] === monthIdx).length
  const repeatPatients = [...patientIds].filter((pid) => patientCallCount[pid] > 1).length

  const periods = { เช้า: 0, บ่าย: 0, เย็น: 0, ดึก: 0 }
  const dows = [0, 0, 0, 0, 0, 0, 0]
  calls.forEach((c) => {
    const m = moment(c.startMs)
    periods[periodOf(m.hour())] += 1
    dows[m.day()] += 1
  })

  return {
    callCount: calls.length,
    patientCount: patientIds.size,
    doctorCount: doctorNames.size,
    activeDayCount: days.size,
    totalSec,
    avgPerCallSec: calls.length ? totalSec / calls.length : 0,
    medianSec: median(durSecs),
    avgPerPatientSec: patientIds.size ? totalSec / patientIds.size : 0,
    avgPerActiveDaySec: days.size ? totalSec / days.size : 0,
    maxSec: durSecs.length ? Math.max(...durSecs) : 0,
    minSec: durSecs.length ? Math.min(...durSecs) : 0,
    newPatients,
    repeatPatients,
    periods,
    dows,
  }
}

// ===== Sheet 1: ภาพรวมปี (Tier 1 เท่านั้น - สำหรับลูกค้า) =====
const buildSheet1 = (year, callDetails, monthlyAgg, yearAgg) => {
  const rows = []
  const merges = []

  // Title bar (row 0)
  rows.push([
    tCell(`รายงานเวลาคุยกับแพทย์ ปี ${year}`, STYLE.title),
    null, null, null, null, null, null,
  ])
  merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 6 } })

  // Subtitle (row 1) - report generated date
  rows.push([
    tCell(`สร้างเมื่อ ${moment().format('D MMMM YYYY HH:mm')}`, STYLE.subtitle),
    null, null, null, null, null, null,
  ])
  merges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: 6 } })

  // empty (row 2)
  rows.push([])

  // KPI cards (rows 3-6) - 5 cards in single row of label/value pairs
  // layout: 5 cards in row 3 (labels) and row 4 (values), each card spans 1 column with column spacer
  // Simpler: 5 columns A-E for cards
  const kpis = [
    { label: 'จำนวนสาย', value: yearAgg.callCount, fmt: 'int' },
    { label: 'ผู้ป่วย', value: yearAgg.patientCount, fmt: 'int' },
    { label: 'รวมนาที', value: minFmt(yearAgg.totalSec), fmt: 'num' },
    { label: 'เฉลี่ย/สาย', value: minFmt(yearAgg.avgPerCallSec), fmt: 'num' },
    { label: 'เฉลี่ย/ผู้ป่วย', value: minFmt(yearAgg.avgPerPatientSec), fmt: 'num' },
  ]
  // labels row (row 3) - แต่ละ KPI กว้าง 2 columns (มี gap)
  // ใช้ A B (KPI1), C D (KPI2)... → ต้องเลื่อนคอลัมน์
  // ง่ายกว่า: ใช้ติดกัน 5 columns A-E, ปล่อย F G ว่าง
  const labelRow = []
  const valueRow = []
  kpis.forEach((k) => {
    labelRow.push(tCell(k.label, STYLE.kpiLabel))
    valueRow.push(nCell(k.value, STYLE.kpiValue))
  })
  // pad to 7 columns
  while (labelRow.length < 7) { labelRow.push(null); valueRow.push(null) }
  rows.push(labelRow)
  rows.push(valueRow)

  // empty (row 5)
  rows.push([])

  // Section: สรุปรายเดือน (row 6)
  rows.push([
    tCell('สรุปรายเดือน', STYLE.section),
    null, null, null, null, null, null,
  ])
  merges.push({ s: { r: rows.length - 1, c: 0 }, e: { r: rows.length - 1, c: 6 } })

  // Table header (row 7)
  const tableHeaders = ['เดือน', 'จำนวนสาย', 'ผู้ป่วย', 'แพทย์', 'วันทำการ', 'รวมนาที', 'เฉลี่ย/สาย']
  rows.push(tableHeaders.map((h) => tCell(h, STYLE.th)))

  // Month rows (rows 8-19)
  monthlyAgg.forEach((m, i) => {
    const zebra = i % 2 === 1
    rows.push([
      tCell(m.label, STYLE.td(zebra)),
      nCell(m.callCount, STYLE.tdInt(zebra)),
      nCell(m.patientCount, STYLE.tdInt(zebra)),
      nCell(m.doctorCount, STYLE.tdInt(zebra)),
      nCell(m.activeDayCount, STYLE.tdInt(zebra)),
      nCell(minFmt(m.totalSec), STYLE.tdNum(zebra)),
      nCell(minFmt(m.avgPerCallSec), STYLE.tdNum(zebra)),
    ])
  })

  // Total row
  rows.push([
    tCell('รวมทั้งปี', STYLE.total),
    nCell(yearAgg.callCount, STYLE.totalInt),
    nCell(yearAgg.patientCount, STYLE.totalInt),
    nCell(yearAgg.doctorCount, STYLE.totalInt),
    nCell(yearAgg.activeDayCount, STYLE.totalInt),
    nCell(minFmt(yearAgg.totalSec), STYLE.totalNum),
    nCell(minFmt(yearAgg.avgPerCallSec), STYLE.totalNum),
  ])

  // empty
  rows.push([])

  // Section: Top 10 ผู้ป่วยทั้งปี
  rows.push([
    tCell('Top 10 ผู้ป่วย (เวลาคุยรวมสูงสุดทั้งปี)', STYLE.section),
    null, null, null, null, null, null,
  ])
  merges.push({ s: { r: rows.length - 1, c: 0 }, e: { r: rows.length - 1, c: 6 } })

  // Top 10 header
  rows.push([
    tCell('อันดับ', STYLE.th),
    tCell('ชื่อผู้ป่วย', STYLE.th),
    tCell('แพทย์ล่าสุด', STYLE.th),
    tCell('จำนวนสาย', STYLE.th),
    tCell('รวมนาที', STYLE.th),
    tCell('เฉลี่ย/สาย', STYLE.th),
    null,
  ])

  // Compute top 10 patients
  const byPatient = {}
  callDetails.forEach((c) => {
    if (!c.patientId) return
    if (!byPatient[c.patientId]) {
      byPatient[c.patientId] = { name: c.patientName, doctor: c.doctorName, calls: [] }
    }
    byPatient[c.patientId].calls.push(c)
    byPatient[c.patientId].doctor = c.doctorName || byPatient[c.patientId].doctor // ใช้ล่าสุด
  })
  const top10 = Object.values(byPatient)
    .map((p) => ({
      name: p.name,
      doctor: p.doctor || '-',
      count: p.calls.length,
      totalSec: p.calls.reduce((s, c) => s + c.durSec, 0),
    }))
    .sort((a, b) => b.totalSec - a.totalSec)
    .slice(0, 10)

  top10.forEach((p, i) => {
    const zebra = i % 2 === 1
    rows.push([
      nCell(i + 1, STYLE.tdInt(zebra)),
      tCell(p.name, STYLE.td(zebra)),
      tCell(p.doctor, STYLE.td(zebra)),
      nCell(p.count, STYLE.tdInt(zebra)),
      nCell(minFmt(p.totalSec), STYLE.tdNum(zebra)),
      nCell(minFmt(p.totalSec / p.count), STYLE.tdNum(zebra)),
      null,
    ])
  })

  return buildSheet(rows, {
    cols: [
      // ปรับให้พอดีกับทุก section: KPI labels / Monthly table headers / Top 10 patient/doctor names
      { wch: 14 }, // A: เดือน, KPI "จำนวนสาย", อันดับ
      { wch: 30 }, // B: KPI "ผู้ป่วย" + Top 10 ชื่อผู้ป่วย (ชื่อไทยยาว)
      { wch: 26 }, // C: KPI "รวมนาที" + Top 10 แพทย์
      { wch: 14 }, // D: KPI "เฉลี่ย/สาย" / Monthly แพทย์
      { wch: 18 }, // E: KPI "เฉลี่ย/ผู้ป่วย" 13 ตัว / Monthly วันทำการ
      { wch: 14 }, // F: Monthly รวมนาที
      { wch: 14 }, // G: Monthly เฉลี่ย/สาย
    ],
    merges,
    rows: [
      { hpt: 38 }, // title
      { hpt: 18 }, // subtitle
      undefined,
      { hpt: 22 }, // kpi label
      { hpt: 40 }, // kpi value (ใหญ่)
    ],
  })
}

// ===== Sheet 2: รายเดือน (Tier 1+2+3 - มี Engagement+Distribution+median) =====
const buildSheet2 = (year, monthlyAgg) => {
  const rows = []
  const merges = []

  monthlyAgg.forEach((m) => {
    // Section header
    rows.push([
      tCell(`เดือน ${m.label}`, STYLE.section),
      null, null, null, null, null, null, null, null,
    ])
    merges.push({ s: { r: rows.length - 1, c: 0 }, e: { r: rows.length - 1, c: 8 } })

    // KPI summary line
    rows.push([
      tCell('จำนวนสาย', STYLE.kpiLabel),
      tCell('ผู้ป่วย', STYLE.kpiLabel),
      tCell('แพทย์', STYLE.kpiLabel),
      tCell('วันทำการ', STYLE.kpiLabel),
      tCell('รวมนาที', STYLE.kpiLabel),
      tCell('เฉลี่ย/สาย', STYLE.kpiLabel),
      tCell('median', STYLE.kpiLabel),
      tCell('ใหม่/ซ้ำ', STYLE.kpiLabel),
      tCell('นานสุด/สั้นสุด', STYLE.kpiLabel),
    ])
    rows.push([
      nCell(m.callCount, STYLE.kpiValue),
      nCell(m.patientCount, STYLE.kpiValue),
      nCell(m.doctorCount, STYLE.kpiValue),
      nCell(m.activeDayCount, STYLE.kpiValue),
      nCell(minFmt(m.totalSec), STYLE.kpiValue),
      nCell(minFmt(m.avgPerCallSec), STYLE.kpiValue),
      nCell(minFmt(m.medianSec), STYLE.kpiValue),
      tCell(`${m.newPatients} / ${m.repeatPatients}`, STYLE.kpiValue),
      tCell(`${minFmt(m.maxSec)} / ${minFmt(m.minSec)}`, STYLE.kpiValue),
    ])

    // Distribution line
    rows.push([
      tCell(
        `ช่วงเวลา: เช้า ${m.periods.เช้า} · บ่าย ${m.periods.บ่าย} · เย็น ${m.periods.เย็น} · ดึก ${m.periods.ดึก}    `
        + `วันในสัปดาห์: จ ${m.dows[1]} · อ ${m.dows[2]} · พ ${m.dows[3]} · พฤ ${m.dows[4]} · ศ ${m.dows[5]} · ส ${m.dows[6]} · อา ${m.dows[0]}`,
        { font: { sz: 10, color: { rgb: COLORS.subText } }, alignment: { vertical: 'center' } },
      ),
      null, null, null, null, null, null, null, null,
    ])
    merges.push({ s: { r: rows.length - 1, c: 0 }, e: { r: rows.length - 1, c: 8 } })

    rows.push([]) // spacer

    // ====== Per-patient table (เฉพาะถ้ามีข้อมูล) ======
    if (m.callCount) {
      rows.push([tCell('สรุปต่อผู้ป่วย', { font: { bold: true, sz: 11, color: { rgb: COLORS.primary } } }), null, null, null, null, null, null, null, null])
      rows.push([
        tCell('ชื่อผู้ป่วย', STYLE.th),
        tCell('แพทย์', STYLE.th),
        tCell('จำนวนสาย', STYLE.th),
        tCell('รวมนาที', STYLE.th),
        tCell('เฉลี่ย/สาย', STYLE.th),
        tCell('วันที่คุย', STYLE.th),
        tCell('สายแรก', STYLE.th),
        tCell('สายสุดท้าย', STYLE.th),
        null,
      ])

      const patAgg = {}
      m.calls.forEach((c) => {
        const k = c.patientId || c.patientName
        if (!patAgg[k]) patAgg[k] = { name: c.patientName, doctor: c.doctorName || '-', calls: [] }
        patAgg[k].calls.push(c)
      })

      const sorted = Object.values(patAgg).sort(
        (a, b) => b.calls.reduce((s, c) => s + c.durSec, 0) - a.calls.reduce((s, c) => s + c.durSec, 0),
      )
      sorted.forEach((p, i) => {
        const zebra = i % 2 === 1
        const sumSec = p.calls.reduce((s, c) => s + c.durSec, 0)
        const days = [...new Set(p.calls.map((c) => moment(c.startMs).format('D')))]
          .sort((a, b) => Number(a) - Number(b)).join(', ')
        const minStart = Math.min(...p.calls.map((c) => c.startMs))
        const maxEnd = Math.max(...p.calls.map((c) => c.startMs + c.durSec * 1000))
        rows.push([
          tCell(p.name, STYLE.td(zebra)),
          tCell(p.doctor, STYLE.td(zebra)),
          nCell(p.calls.length, STYLE.tdInt(zebra)),
          nCell(minFmt(sumSec), STYLE.tdNum(zebra)),
          nCell(minFmt(sumSec / p.calls.length), STYLE.tdNum(zebra)),
          tCell(days, STYLE.td(zebra)),
          tCell(moment(minStart).format('DD/MM/YYYY HH:mm'), STYLE.td(zebra)),
          tCell(moment(maxEnd).format('DD/MM/YYYY HH:mm'), STYLE.td(zebra)),
          null,
        ])
      })

      rows.push([]) // spacer

      // ====== Per-call table (จัด ผู้ป่วย/แพทย์ ไว้ A-B เพื่อใช้คอลัมน์กว้างร่วมกับตารางบน) ======
      rows.push([tCell('รายการสายในเดือน', { font: { bold: true, sz: 11, color: { rgb: COLORS.primary } } }), null, null, null, null, null, null, null, null])
      rows.push([
        tCell('ผู้ป่วย', STYLE.th),
        tCell('แพทย์', STYLE.th),
        tCell('วันที่', STYLE.th),
        tCell('เริ่ม', STYLE.th),
        tCell('จบ', STYLE.th),
        tCell('นาที', STYLE.th),
        tCell('ช่วง', STYLE.th),
        tCell('วัน', STYLE.th),
        null,
      ])
      m.calls.slice().sort((a, b) => a.startMs - b.startMs).forEach((c, i) => {
        const zebra = i % 2 === 1
        const start = moment(c.startMs)
        const end = moment(c.startMs + c.durSec * 1000)
        rows.push([
          tCell(c.patientName || '-', STYLE.td(zebra)),
          tCell(c.doctorName || '-', STYLE.td(zebra)),
          tCell(start.format('DD/MM/YYYY'), STYLE.td(zebra)),
          tCell(start.format('HH:mm'), STYLE.td(zebra)),
          tCell(end.format('HH:mm'), STYLE.td(zebra)),
          nCell(minFmt(c.durSec), STYLE.tdNum(zebra)),
          tCell(periodOf(start.hour()), STYLE.td(zebra)),
          tCell(DOW_TH_FULL[start.day()], STYLE.td(zebra)),
          null,
        ])
      })
    } else {
      rows.push([tCell('— ไม่มีข้อมูลในเดือนนี้ —', { font: { italic: true, color: { rgb: COLORS.subText } }, alignment: { horizontal: 'center' } }), null, null, null, null, null, null, null, null])
      merges.push({ s: { r: rows.length - 1, c: 0 }, e: { r: rows.length - 1, c: 8 } })
    }

    rows.push([]) // spacer
    rows.push([]) // spacer
  })

  return buildSheet(rows, {
    cols: [
      // A,B: ผู้ป่วย/แพทย์ (กว้างที่สุดทั้งใน per-patient table + per-call table + KPI line)
      { wch: 28 }, // A: ชื่อผู้ป่วย / KPI "จำนวนสาย"
      { wch: 24 }, // B: แพทย์ / KPI "ผู้ป่วย"
      // C-H: ทั้ง KPI labels และ ตารางย่อย ใช้ความกว้างพอดี header
      { wch: 14 }, // C: KPI "แพทย์" / per-patient "จำนวนสาย" / per-call "วันที่"
      { wch: 14 }, // D: KPI "วันทำการ" / per-patient "รวมนาที" / per-call "เริ่ม"
      { wch: 14 }, // E: KPI "รวมนาที" / per-patient "เฉลี่ย/สาย" / per-call "จบ"
      { wch: 14 }, // F: KPI "เฉลี่ย/สาย" / per-patient "วันที่คุย" / per-call "นาที"
      { wch: 18 }, // G: KPI "median" / per-patient "สายแรก" (DD/MM/YYYY HH:mm = 16 ตัว) / per-call "ช่วง"
      { wch: 18 }, // H: KPI "ใหม่/ซ้ำ" / per-patient "สายสุดท้าย" / per-call "วัน" (full name)
      { wch: 20 }, // I: KPI "นานสุด/สั้นสุด" (16 ตัว) - คอลัมน์นี้ใช้เฉพาะ KPI line
    ],
    merges,
  })
}

// ===== Sheet 3: รายละเอียดต่อสาย (flat + autofilter) =====
const buildSheet3 = (callDetails) => {
  const rows = []
  rows.push([
    tCell('เดือน', STYLE.th),
    tCell('วันที่', STYLE.th),
    tCell('เวลาเริ่ม', STYLE.th),
    tCell('เวลาจบ', STYLE.th),
    tCell('นาที', STYLE.th),
    tCell('ผู้ป่วย', STYLE.th),
    tCell('แพทย์', STYLE.th),
    tCell('วัน', STYLE.th),
    tCell('ช่วงเวลา', STYLE.th),
  ])

  callDetails.slice().sort((a, b) => a.startMs - b.startMs).forEach((c, i) => {
    const zebra = i % 2 === 1
    const start = moment(c.startMs)
    const end = moment(c.startMs + c.durSec * 1000)
    rows.push([
      tCell(start.format('MM/YYYY'), STYLE.td(zebra)),
      tCell(start.format('DD/MM/YYYY'), STYLE.td(zebra)),
      tCell(start.format('HH:mm'), STYLE.td(zebra)),
      tCell(end.format('HH:mm'), STYLE.td(zebra)),
      nCell(minFmt(c.durSec), STYLE.tdNum(zebra)),
      tCell(c.patientName || '-', STYLE.td(zebra)),
      tCell(c.doctorName || '-', STYLE.td(zebra)),
      tCell(DOW_TH_FULL[start.day()], STYLE.td(zebra)),
      tCell(periodOf(start.hour()), STYLE.td(zebra)),
    ])
  })

  return buildSheet(rows, {
    cols: [
      { wch: 10 }, // เดือน (MM/YYYY)
      { wch: 13 }, // วันที่ (DD/MM/YYYY)
      { wch: 11 }, // เวลาเริ่ม
      { wch: 11 }, // เวลาจบ
      { wch: 9 },  // นาที
      { wch: 30 }, // ผู้ป่วย (ชื่อไทยยาว)
      { wch: 24 }, // แพทย์
      { wch: 13 }, // วัน (full: "พฤหัสบดี" 8 ตัว)
      { wch: 11 }, // ช่วงเวลา
    ],
    autofilter: { ref: `A1:I${rows.length}` },
    freeze: { row: 1 },
  })
}

// ===== Main export =====
export const exportYearlyReport = (year, callDetails) => {
  const yearStr = String(year)

  // bucket per month
  const monthlyCalls = Array.from({ length: 12 }, () => [])
  callDetails.forEach((c) => {
    const m = moment(c.startMs)
    if (m.year() !== year) return
    monthlyCalls[m.month()].push(c)
  })

  // patient first appearance month (for new patient count)
  const patientFirstMonth = {}
  callDetails.forEach((c) => {
    if (!c.patientId) return
    const m = moment(c.startMs).month()
    if (!(c.patientId in patientFirstMonth) || patientFirstMonth[c.patientId] > m) {
      patientFirstMonth[c.patientId] = m
    }
  })

  const monthlyAgg = monthlyCalls.map((calls, i) => ({
    monthNum: i + 1,
    label: `${String(i + 1).padStart(2, '0')}/${yearStr}`,
    calls,
    ...computeMonth(calls, i, patientFirstMonth),
  }))

  // Year aggregate
  const durSecs = callDetails.map((c) => c.durSec)
  const totalSec = durSecs.reduce((s, v) => s + v, 0)
  const patientIdsAll = new Set(callDetails.map((c) => c.patientId).filter(Boolean))
  const yearAgg = {
    callCount: callDetails.length,
    patientCount: patientIdsAll.size,
    doctorCount: new Set(callDetails.map((c) => c.doctorName).filter(Boolean)).size,
    activeDayCount: new Set(callDetails.map((c) => moment(c.startMs).format('YYYY-MM-DD'))).size,
    totalSec,
    avgPerCallSec: callDetails.length ? totalSec / callDetails.length : 0,
    avgPerPatientSec: patientIdsAll.size ? totalSec / patientIdsAll.size : 0,
  }

  const ws1 = buildSheet1(yearStr, callDetails, monthlyAgg, yearAgg)
  const ws2 = buildSheet2(yearStr, monthlyAgg)
  const ws3 = buildSheet3(callDetails)

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws1, `ภาพรวมปี ${yearStr}`)
  XLSX.utils.book_append_sheet(wb, ws2, 'รายเดือน')
  XLSX.utils.book_append_sheet(wb, ws3, 'รายละเอียดต่อสาย')

  XLSX.writeFile(wb, `รายงานเวลาคุยกับแพทย์_ปี${yearStr}.xlsx`)
}
